import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as figmaService from '../services/figmaService';

export const figmaKeys = {
  connection: (projectId: string) => ['figma', 'connection', projectId] as const,
  files: (projectId: string) => ['figma', 'files', projectId] as const,
  browse: (projectId: string) => ['figma', 'browse', projectId] as const,
};

export function useFigmaConnection(projectId: string) {
  return useQuery({
    queryKey: figmaKeys.connection(projectId),
    queryFn: () => figmaService.getConnectionStatus(projectId),
    enabled: !!projectId,
  });
}

export function useFigmaFiles(projectId: string, enabled = true) {
  return useQuery({
    queryKey: figmaKeys.files(projectId),
    queryFn: () => figmaService.getMappedFiles(projectId),
    enabled: !!projectId && enabled,
  });
}

export function useFigmaBrowse(projectId: string, enabled = false) {
  return useQuery({
    queryKey: figmaKeys.browse(projectId),
    queryFn: () => figmaService.browseFigmaTeamFiles(projectId),
    enabled: !!projectId && enabled,
    staleTime: 5 * 60 * 1000, // 5분 캐싱 (서버 Redis와 동일)
  });
}

export function useDisconnectFigma() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => figmaService.disconnectFigma(projectId),
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({ queryKey: figmaKeys.connection(projectId) });
      queryClient.invalidateQueries({ queryKey: figmaKeys.files(projectId) });
    },
  });
}

export function useAddFileMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; fileKey: string; fileName: string }) =>
      figmaService.addFileMapping(params.projectId, params.fileKey, params.fileName),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: figmaKeys.files(params.projectId) });
    },
  });
}

export function useRemoveFileMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; figmaFileId: string }) =>
      figmaService.removeFileMapping(params.projectId, params.figmaFileId),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: figmaKeys.files(params.projectId) });
    },
  });
}

export function useUpdateFigmaTeamId() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { projectId: string; figmaTeamId: string }) =>
      figmaService.updateTeamId(params.projectId, params.figmaTeamId),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: figmaKeys.connection(params.projectId) });
    },
  });
}
